<template>
	<v-card
		:width="mobile ? null : 380"
		:max-width="mobile ? null : 380"
		:class="{ 'd-flex flex-column': mobile }"
		:style="mobile ? 'height: 100vh' : ''"
		flat
	>
		<div class="d-flex align-center pa-2">
			<span class="font-weight-bold">{{ $t('ticketing.notifications') }}</span>
			<v-spacer />
			<v-btn
				v-if="unreadCount > 0"
				x-small
				text
				@click="$emit('mark-all-read')"
			>
				{{ $t('ticketing.mark-all-read') }}
			</v-btn>
			<v-btn
				v-if="notifications.length"
				x-small
				text
				color="red"
				@click="$emit('clear-all')"
			>
				{{ $t('ticketing.clear-all') }}
			</v-btn>
			<v-btn
				v-if="mobile"
				icon
				small
				class="ml-1"
				@click="$emit('close')"
			>
				<v-icon>mdi-close</v-icon>
			</v-btn>
		</div>
		<v-divider />
		<div v-if="!notifications.length" class="pa-4 text-caption grey--text text-center">
			{{ $t('ticketing.no-notifications') }}
		</div>
		<v-list
			dense
			:max-height="mobile ? null : 400"
			:style="mobile ? 'flex: 1 1 auto; overflow-y: auto;' : ''"
			class="overflow-y-auto notif-list"
		>
			<v-list-item
				v-for="n in notifications"
				:key="n.id"
				:class="{ 'unread-notif': !n.read }"
				class="notif-item"
				@click="$emit('notification-click', n)"
			>
				<v-list-item-avatar size="22" class="my-1 mr-2">
					<v-icon small :color="notificationColor(n.type)">{{ notificationIcon(n.type) }}</v-icon>
				</v-list-item-avatar>
				<v-list-item-content class="py-1">
					<v-list-item-subtitle
						v-if="n.ticketTitle || n.organizationName"
						class="notif-context"
					>
						<div class="notif-context__row">
							<span class="notif-emoji">🎟️</span>
							<span v-if="n.ticketTitle" class="notif-ticket text-truncate">{{ n.ticketTitle }}</span>
							<span class="notif-time" :title="formatTimeAbsolute(n.createdAt)">{{ formatTime(n.createdAt) }}</span>
						</div>
						<div v-if="n.organizationName" class="notif-org text-truncate">
							<span class="notif-emoji">🏢</span><span>{{ n.organizationName }}</span>
						</div>
					</v-list-item-subtitle>
					<v-list-item-title class="notif-title">
						<template v-if="n.type === 'ticket_updated' && n.field">
							<span class="font-weight-medium">{{ n.actorName || $t('ticketing.someone') }}</span>
							{{ ' ' + $t('ticketing.activity-changed', { field: $t('ticketing.field.' + n.field) }) }}
							<span v-if="n.fromValue != null && n.fromValue !== ''" class="grey--text text--darken-1">
								{{ ' ' + $t('ticketing.activity-from') }}
							</span>
							<span v-if="n.fromValue != null && n.fromValue !== ''" class="activity-value">{{ n.fromValue }}</span>
							<span class="grey--text text--darken-1">
								{{ ' ' + $t('ticketing.activity-to') }}
							</span>
							<span class="activity-value">{{ n.toValue || '—' }}</span>
						</template>
						<template v-else-if="n.type === 'attachments_added'">
							<span class="font-weight-medium">{{ n.actorName || $t('ticketing.someone') }}</span>
							{{ ' ' + $t('ticketing.activity-attachments-added') }}
							<span class="activity-value">{{ n.toValue }}</span>
						</template>
						<template v-else-if="n.type === 'attachments_removed'">
							<span class="font-weight-medium">{{ n.actorName || $t('ticketing.someone') }}</span>
							{{ ' ' + $t('ticketing.activity-attachments-removed') }}
							<span class="activity-value">{{ n.toValue }}</span>
						</template>
						<template v-else-if="n.type === 'watcher_added'">
							<span class="font-weight-medium">{{ n.actorName || $t('ticketing.someone') }}</span>
							{{ ' ' + $t('ticketing.activity-watcher-added') }}
							<span class="activity-value">{{ n.toValue }}</span>
						</template>
						<template v-else-if="n.type === 'watcher_removed'">
							<span class="font-weight-medium">{{ n.actorName || $t('ticketing.someone') }}</span>
							{{ ' ' + $t('ticketing.activity-watcher-removed') }}
							<span class="activity-value">{{ n.toValue }}</span>
						</template>
						<template v-else>
							{{ n.message }}
						</template>
						<span v-if="!(n.ticketTitle || n.organizationName)" class="notif-time-inline" :title="formatTimeAbsolute(n.createdAt)">{{ formatTime(n.createdAt) }}</span>
					</v-list-item-title>
				</v-list-item-content>
				<v-list-item-action class="my-0 ml-1">
					<v-btn icon x-small @click.stop="$emit('remove', n.id)">
						<v-icon x-small>mdi-close</v-icon>
					</v-btn>
				</v-list-item-action>
			</v-list-item>
		</v-list>
	</v-card>
</template>

<script>
import moment from "moment";

export default {
	name: "TicketingNotificationsPanel",

	props: {
		notifications: {
			type: Array,
			default: () => []
		},

		unreadCount: {
			type: Number,
			default: 0
		},

		mobile: {
			type: Boolean,
			default: false
		}
	},

	methods: {
		notificationIcon(type) {
			return {
				"ticket_created": "mdi-ticket-outline",
				"ticket_updated": "mdi-pencil-outline",
				"new_message": "mdi-message-text-outline",
				"mention": "mdi-at",
				"reaction": "mdi-emoticon-happy-outline",
				"added_as_watcher": "mdi-eye-plus-outline",
				"removed_as_watcher": "mdi-eye-minus-outline",
				"watcher_added": "mdi-eye-plus-outline",
				"watcher_removed": "mdi-eye-minus-outline",
				"attachments_added": "mdi-paperclip-plus",
				"attachments_removed": "mdi-paperclip-minus",
				"todo_added": "mdi-checkbox-marked-circle-plus-outline",
				"todo_edited": "mdi-pencil-outline",
				"todo_completed": "mdi-checkbox-marked-circle",
				"todo_uncompleted": "mdi-checkbox-blank-circle-outline",
				"todo_deleted": "mdi-delete-outline",
				"org_added": "mdi-domain-plus",
				"org_removed": "mdi-domain-remove",
				"org_contact_updated": "mdi-account-switch-outline",
				"ticket_unassigned": "mdi-account-question-outline",
				"follow_up_logged": "mdi-phone-log-outline"
			}[type] || "mdi-bell-outline";
		},

		notificationColor(type) {
			return {
				"ticket_created": "success",
				"ticket_updated": "orange",
				"new_message": "primary",
				"mention": "deep-purple",
				"reaction": "green",
				"added_as_watcher": "teal",
				"removed_as_watcher": "red",
				"watcher_added": "teal",
				"watcher_removed": "red",
				"attachments_added": "green",
				"attachments_removed": "red",
				"todo_added": "green",
				"todo_edited": "primary",
				"todo_completed": "green",
				"todo_uncompleted": "grey",
				"todo_deleted": "red",
				"org_added": "green",
				"org_removed": "red",
				"org_contact_updated": "primary",
				"ticket_unassigned": "orange",
				"follow_up_logged": "primary"
			}[type] || "grey";
		},

		formatTime(ts) {
			const date = this.toDate(ts);

			return date ? moment(date).fromNow() : "";
		},

		formatTimeAbsolute(ts) {
			const date = this.toDate(ts);

			return date ? moment(date).format("YYYY-MM-DD HH:mm") : "";
		},

		toDate(ts) {
			if (!ts) return null;
			if (ts.toDate) return ts.toDate();
			if (typeof ts === "number") return new Date(ts);
			if (typeof ts === "string") return new Date(ts);
			if (ts instanceof Date) return ts;
			return null;
		}
	}
};
</script>

<style scoped>
.notif-list {
	padding: 0;
}

.notif-item {
	min-height: 44px;
	padding-top: 4px !important;
	padding-bottom: 4px !important;
}

.notif-item.unread-notif {
	background-color: rgba(25, 118, 210, 0.06);
}

.notif-context {
	display: flex;
	flex-direction: column;
	align-items: stretch;
	min-width: 0;
	color: rgba(0, 0, 0, 0.87) !important;
	font-size: 12px !important;
	line-height: 1.25;
	margin-bottom: 2px !important;
}

.notif-context__row {
	display: flex;
	align-items: center;
	gap: 4px;
	min-width: 0;
}

.notif-context__row .notif-ticket {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
}

.notif-emoji {
	font-size: 12px;
	line-height: 1;
	flex-shrink: 0;
}

.notif-org {
	font-weight: 700;
	color: rgba(0, 0, 0, 0.87);
	min-width: 0;
	overflow: hidden;
	display: flex;
	align-items: center;
	gap: 4px;
}

.notif-time {
	margin-left: auto;
	font-size: 11px;
	color: rgba(0, 0, 0, 0.54);
	flex-shrink: 0;
	white-space: nowrap;
}

.notif-time-inline {
	margin-left: 6px;
	font-size: 11px;
	color: rgba(0, 0, 0, 0.54);
	white-space: nowrap;
}

.notif-title {
	white-space: normal !important;
	font-size: 13px;
	line-height: 1.3;
}

.activity-value {
	font-weight: 500;
}
</style>
